import * as React from 'react';
import { Modal, ModalBody } from 'reactstrap';
import {
    Prospect,
    TourFeedbackDto,
    getTourFeedback,
    sendTourSignupEmail,
    updateTour,
    deleteTour,
    PhoneAndEmail,
    updatePhoneAndEmail,
    markTourCompleted,
    toggleProspectExclusion,
    markVirtualTourCompleted,
    updateApplicationStatus
} from '../../../../api/Prospect';
import * as moment from 'moment';
import { ApiResult } from '../../../../api/Shared';
import { useFetch } from '../../../../hooks/useFetch';
import Button from '../../../common/form/Button';
import { formatPhoneNumber } from '../../../../utilities/Utils';
import { useState, useEffect } from 'react';
import { postAction } from '../../../../hooks/postAction';
import { TourStatuses, VirtualTourStatus } from '../../../../utilities/Enums';
import { Event } from '../../../../api/Property';
import FormValidationDisplay from '../../../common/form/ValidationDisplay';
import LoadingOverlay from '../../../common/LoadingOverlay';
import { userHasPropertyAccess, userIsSystemAdmin } from '../../../../utilities/identity';
import { ProspectInfoRowEditable } from '../../admin/dashboardWidgets/prospectInfoRowEditable';
import { AdditionalResidentCard } from '../../admin/dashboardWidgets/additionalResidentCard';
import TourTimeSlotInput from '../../../common/TourTimeSlotInput';
import { ProspectAuditInfo } from './prospectAuditInfo';
import TourAccessInformation from '../../../common/TourAccessInformation';
import { ProspectInfoRow } from './prospectInfoRow';
import {userIsCompanyDashboardUser, userIsPropertyDashboardUser, userIsSystemDashboardUser } from '../../../../utilities/identity';

interface PopupProps {
    prospectToken: string | null;
    prospect: Prospect | undefined;
    propertyId: number | null;
    setSelectedProspectToken: (prospectToken: string | null) => void;
    updateSelectedProspect: (newProspect: Prospect | null, cancelled: boolean) => void;
    addExcludedProspect?: (prospect: Prospect) => void;
}

export const ProspectPopup = ({
    prospectToken,
    prospect,
    propertyId,
    setSelectedProspectToken,
    updateSelectedProspect,
    addExcludedProspect
}: PopupProps) => {
    let canEditTours = userHasPropertyAccess();

    const fake = () =>
        new Promise<ApiResult<TourFeedbackDto | null>>(resolve =>
            resolve({ success: false, error: 'No tour selected', data: null })
        );
    const finalFunc =
        prospect && prospect.tourInfo.id
            ? () => getTourFeedback(prospect.tourInfo.id as number)
            : fake;

    const { data: tourFeedback, loading } = useFetch(finalFunc, [prospectToken], null);
    const sendEmailButtonIsVisible =
        prospect &&
        !(prospect.contactInfo.virtualTourStatus !== VirtualTourStatus.None) &&
        !(prospect.tourInfo && prospect.tourInfo.id);
    const [isSending, setIsSending] = useState(false);
    const [messageSent, setMessageSent] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    const [editingTour, setEditingTour] = useState(false);
    const [phoneAndEmailObject, setPhoneAndEmailObject] = useState<PhoneAndEmail | null>(null);
    const [prospectTemp, setProspectTemp] = useState<Prospect | undefined>(prospect);

    const [tourChangeLoading, setTourChangeLoading] = useState(false);
    const [contactInfoChangeLoading, setContactInfoChangeLoading] = useState(false);

    const isUser = userIsCompanyDashboardUser() ||
    userIsPropertyDashboardUser() ||
    userIsSystemDashboardUser();

    useEffect(() => {
        if (prospectTemp === undefined && prospect !== undefined) {
            setProspectTemp(prospect);
        }
    });

    const allowedToEdit =
        prospectTemp &&
        prospectTemp.publicKey &&
        prospectTemp.tourInfo.event &&
        prospectTemp.tourInfo.status !== TourStatuses.Cancelled &&
        prospectTemp.tourInfo.status !== TourStatuses.Complete &&
        prospectTemp.tourInfo.status !== TourStatuses.NoShow;

    let resetMessageState: () => void = () => {
        setIsSending(false);
        setMessageSent(false);
    };

    useEffect(resetMessageState, [prospectToken]);

    let fontSizeStyle = {
        fontSize: '15px'
    };

    const handleIncludeOrExclude = async () => {
        if (prospect !== undefined) {
            setTourChangeLoading(true);
            if (prospect.publicKey === undefined) {
                setErrorMessage('Prospect has no associated ID.');
                setTourChangeLoading(false);
                return;
            }

            let excludeResult = await toggleProspectExclusion(prospect.publicKey);

            if (excludeResult.success) {
                prospect.contactInfo.excludeFromStatistics =
                    !prospect.contactInfo.excludeFromStatistics;
                if (addExcludedProspect !== undefined) {
                    addExcludedProspect(prospect);
                }
                updateSelectedProspect(prospect, false);
                setErrorMessage(undefined);
                setSelectedProspectToken(null);
            } else if (excludeResult.error) {
                setErrorMessage(excludeResult.error);
            }

            setTourChangeLoading(false);
        }
    };

    const closePopup = () => {
        setSelectedProspectToken(null);
        setEditingTour(false);
        setProspectTemp(undefined);
        setErrorMessage(undefined);
        setPhoneAndEmailObject(null);
    };

    return (
        <Modal isOpen={prospectToken !== null} toggle={closePopup} className="prospect-popup">
            <LoadingOverlay show={tourChangeLoading} inBootstrapColumn={false} />
            <ModalBody>
                {!prospect ? null : (
                    <>
                        <h3>Prospect Information:</h3>
                        <div>
                            {prospect.contactInfo.firstName} {prospect.contactInfo.lastName}
                        </div>
                        <div className="mb-4">
                            {prospectToken !== null && (
                                <ProspectAuditInfo publicKey={prospectToken} />
                            )}
                        </div>

                        <div style={fontSizeStyle}>
                            {prospectTemp &&
                                prospectTemp.tourInfo &&
                                propertyId &&
                                (editingTour ? (
                                    <TourTimeSlotInput
                                        event={prospectTemp.tourInfo.event}
                                        date={prospectTemp.tourInfo.date}
                                        propertyId={propertyId}
                                        onDateChanged={(newDate: string) =>
                                            setProspectTemp({
                                                ...prospectTemp,
                                                tourInfo: {
                                                    ...prospectTemp.tourInfo,
                                                    date: newDate,
                                                    event: undefined
                                                }
                                            })
                                        }
                                        onTimeslotChanged={(newEvent: Event) =>
                                            setProspectTemp({
                                                ...prospectTemp,
                                                tourInfo: {
                                                    ...prospectTemp.tourInfo,
                                                    event: newEvent
                                                }
                                            })
                                        }
                                    />
                                ) : (
                                    prospect.tourInfo.event && (
                                        <>
                                            <ProspectInfoRow
                                                label="Tour Date"
                                                value={moment(
                                                    prospect.tourInfo.event.startDateTime
                                                ).format('MM/DD/YYYY')}
                                                optional={false}
                                            />
                                            <ProspectInfoRow
                                                label="Tour Time"
                                                value={
                                                    moment(
                                                        prospect.tourInfo.event.startDateTime
                                                    ).format('h:mm A') +
                                                    '-' +
                                                    moment(
                                                        prospect.tourInfo.event.endDateTime
                                                    ).format('h:mm A')
                                                }
                                                optional={false}
                                            />
                                        </>
                                    )
                                ))}
                        </div>
                        {prospectTemp && allowedToEdit ? (
                            editingTour ? (
                                <div className="clearfix mt-2">
                                    <Button
                                        addlClassName="btn-primary edit-button"
                                        text={'Submit'}
                                        disabled={isUser}
                                        onClick={async () => {
                                            setTourChangeLoading(true);
                                            updateSelectedProspect(prospectTemp, false);

                                            let updateTourResult = await updateTour(
                                                prospectTemp.publicKey as string,
                                                prospectTemp.tourInfo
                                            );

                                            if (updateTourResult.success) {
                                                closePopup();
                                            } else if (updateTourResult.error) {
                                                setErrorMessage(updateTourResult.error);
                                            }
                                            setTourChangeLoading(false);
                                        }}
                                    />
                                    <Button
                                        addlClassName="btn-danger edit-button"
                                        text={'Cancel'}
                                        disabled={isUser}
                                        onClick={() => {
                                            setEditingTour(false);
                                        }}
                                    />
                                </div>
                            ) : (
                                <>
                                    {canEditTours ? (
                                        <>
                                            <div className="clearfix mt-2">
                                                <Button
                                                    addlClassName="btn-primary edit-button"
                                                    text={'Edit'}
                                                    disabled={isUser}
                                                    onClick={() => {
                                                        setEditingTour(true);
                                                    }}
                                                />
                                                <Button
                                                    addlClassName="btn-danger edit-button"
                                                    text={'Delete'}
                                                    disabled={isUser}
                                                    onClick={async () => {
                                                        setTourChangeLoading(true);
                                                        let deleteResult = await deleteTour(
                                                            prospectTemp.publicKey as string,
                                                            prospectTemp.tourInfo
                                                        );

                                                        if (deleteResult.success) {
                                                            updateSelectedProspect(
                                                                prospectTemp,
                                                                true
                                                            );
                                                            closePopup();
                                                        } else if (deleteResult.error) {
                                                            setErrorMessage(deleteResult.error);
                                                        }
                                                        setTourChangeLoading(false);
                                                    }}
                                                />
                                            </div>
                                        </>
                                    ) : null}
                                </>
                            )
                        ) : null}

                        <div style={fontSizeStyle}>
                            {
                                <ProspectInfoRow
                                    label="Type"
                                    value={prospect.contactInfo.tourTypeDisplay}
                                />
                            }
                            {phoneAndEmailObject ? (
                                <div className="position-relative">
                                    <LoadingOverlay
                                        show={contactInfoChangeLoading}
                                        inBootstrapColumn={false}
                                    />
                                    <ProspectInfoRowEditable
                                        label="Email"
                                        value={phoneAndEmailObject.email}
                                        isPhoneInput={false}
                                        onChangeAction={async newValue => {
                                            setPhoneAndEmailObject({
                                                email: newValue,
                                                phoneNumber: phoneAndEmailObject.phoneNumber
                                            });
                                            setErrorMessage(undefined);
                                        }}
                                    />
                                    <ProspectInfoRowEditable
                                        label="Phone Number"
                                        value={phoneAndEmailObject.phoneNumber}
                                        isPhoneInput={true}
                                        onChangeAction={async newValue => {
                                            setPhoneAndEmailObject({
                                                email: phoneAndEmailObject.email,
                                                phoneNumber: newValue
                                            });
                                            setErrorMessage(undefined);
                                        }}
                                    />
                                  
                                    <div className="clearfix mt-2">
                                        <Button
                                            addlClassName="btn-primary edit-button"
                                            text="Save"
                                            disabled={isUser}
                                            onClick={async () => {
                                                if (!phoneAndEmailObject.email.includes('@')) {
                                                    setErrorMessage(
                                                        'Please enter valid email address.'
                                                    );
                                                    return;
                                                }

                                                if (prospectToken !== null) {
                                                    setContactInfoChangeLoading(true);
                                                    let result = await updatePhoneAndEmail(
                                                        prospectToken,
                                                        phoneAndEmailObject
                                                    );
                                                    let newProspect = prospect;
                                                    setContactInfoChangeLoading(false);

                                                    if (
                                                        result.success &&
                                                        result.data &&
                                                        !result.error
                                                    ) {
                                                        newProspect.contactInfo.phone =
                                                            result.data.phoneNumber;
                                                        newProspect.contactInfo.email =
                                                            result.data.email;
                                                        updateSelectedProspect(newProspect, false);
                                                        setPhoneAndEmailObject(null);
                                                        setErrorMessage(undefined);
                                                    } else {
                                                        setErrorMessage(
                                                            result.error ||
                                                            'Unable to update contact info. \
                                                            Please refresh and try again.'
                                                        );
                                                    }
                                                } else {
                                                    setErrorMessage(
                                                        'Unable to update contact info. Please refresh and try again.'
                                                    );
                                                }
                                            }}
                                        />
                                        <Button
                                            addlClassName="btn-danger edit-button"
                                            text="Cancel"
                                            disabled={isUser}
                                            onClick={async () => {
                                                setPhoneAndEmailObject(null);
                                                setErrorMessage(undefined);
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <ProspectInfoRow
                                        label="Email"
                                        value={prospect.contactInfo.email}
                                    />
                                    <ProspectInfoRow
                                        label="Phone Number"
                                        value={formatPhoneNumber(prospect.contactInfo.phone)}
                                    />
                                    <ProspectInfoRow
                                    label="Partner"
                                    value={prospect.contactInfo.partnerName}
                                     />
                                    <div className="clearfix mt-2">
                                        <Button
                                            addlClassName="btn-primary edit-button"
                                            text="Edit Contact Info"
                                            disabled={isUser}
                                            onClick={async () => {
                                                setPhoneAndEmailObject({
                                                    phoneNumber: prospect.contactInfo.phone || '',
                                                    email: prospect.contactInfo.email || ''
                                                });
                                            }}
                                        />
                                    </div>
                                </>
                            )}
                            <ProspectInfoRow
                                label="Contact Preference"
                                value={prospect.contactInfo.contactPreferenceDisplay}
                            />
                            <ProspectInfoRow
                                label="Desired Move-In Date"
                                value={
                                    prospect.contactInfo.moveInDate
                                        ? moment(prospect.contactInfo.moveInDate).format('M/D/YYYY')
                                        : ''
                                }
                            />
                            <ProspectInfoRow
                                label="Pets"
                                value={
                                    prospect.contactInfo.pet !== undefined
                                        ? prospect.contactInfo.pet === true
                                            ? 'Has Pets'
                                            : 'No Pets'
                                        : ''
                                }
                                optional={true}
                            />
                            <ProspectInfoRow
                                label="Source"
                                value={prospect.contactInfo.source}
                                optional={true}
                            />
                            <ProspectInfoRow
                                label="Desired Lease Length"
                                value={
                                    prospect.contactInfo.desiredLeaseLength
                                        ? `${prospect.contactInfo.desiredLeaseLength} months`
                                        : ''
                                }
                                optional={true}
                            />
                            <ProspectInfoRow
                                label="ID Check Status"
                                value={prospect.idCheckStatus}
                                optional={true}
                            />
                            <ProspectInfoRow
                                label="Credit Card Status"
                                value={prospect.creditCardStatus}
                                optional={true}
                            />
                            {prospect.contactInfo.additionalResidents.map(r => (
                                <AdditionalResidentCard res={r} />
                            ))}
                            {userIsSystemAdmin() && (
                                <ProspectInfoRow
                                    label="Excluded From Statistics"
                                    value={
                                        prospect.contactInfo.excludeFromStatistics ? 'Yes' : 'No'
                                    }
                                />
                            )}
                        </div>
                    </>
                )}
                {loading && 'Loading...'}
                {!tourFeedback || loading ? null : (
                    <>
                        {tourFeedback.history.length > 0 ? (
                            <div className="tour-history" style={fontSizeStyle}>
                                <h4>Tour History:</h4>
                                {tourFeedback.history.map(h => (
                                    <li key={h.timestamp}>
                                        <span>{h.timestamp}</span>
                                        <span>{h.location}</span>
                                    </li>
                                ))}
                            </div>
                        ) : null}
                        {
                            <TourAccessInformation
                                accessCodes={tourFeedback.accessCodes}
                                prospectFacingText={false}
                                bmxAccessCodes={tourFeedback.bmxAccessCodes}
                            />
                        }
                        {tourFeedback.feedback.length > 0 && (
                            <div className="tour-feedback" style={fontSizeStyle}>
                                <h4>Tour Feedback:</h4>
                                <table>
                                    <tbody>
                                        {tourFeedback.feedback.map(f => {
                                            return (
                                                <tr key={f.question}>
                                                    <td>
                                                        <img
                                                            className="pl-1 pr-2"
                                                            src={`https://app.tour24now.com/images/${f.type}.png`}
                                                        />
                                                    </td>
                                                    <td>{f.location}</td>
                                                    <td>
                                                        {f.question && (
                                                            <span className="font-weight-bold">
                                                                {f.answer && 'Q: '}
                                                                {f.question}
                                                            </span>
                                                        )}
                                                        {f.question && f.answer && <br />}
                                                        {f.answer && (
                                                            <span>
                                                                {f.question && 'A: '}
                                                                {f.answer}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
                {userIsSystemAdmin() && (
                    <div className="mt-4">
                        {prospect !== undefined && (
                            <Button
                                addlClassName="btn-primary"
                                text={
                                    prospect.contactInfo.excludeFromStatistics
                                        ? 'Include in Statistics'
                                        : 'Exclude from Statistics'
                                }
                                disabled={isUser}
                                onClick={handleIncludeOrExclude}
                                suppressBottomMargin={true}
                            />
                        )}
                    </div>
                )}
                <div className="mt-4 opposite-buttons">
                    {sendEmailButtonIsVisible && prospect && prospect.publicKey ? (
                        <Button
                            addlClassName="btn-primary email-button"
                            text={
                                messageSent
                                    ? 'Sent!'
                                    : isSending
                                        ? 'Sending...'
                                        : 'Send Tour Signup Email'
                            }
                            disabled={isUser}
                            onClick={() =>
                                postAction({
                                    setIsInProgress: setIsSending,
                                    setActionComplete: setMessageSent,
                                    setErrorMessage: setErrorMessage,
                                    action: () => sendTourSignupEmail(prospect.publicKey as string)
                                    // we're already checking above that prospect.publicKey is not undefined
                                })
                            }
                        />
                    ) : null}
                    {prospect &&
                        prospect.contactInfo.virtualTourStatus !== VirtualTourStatus.None &&
                        prospect.contactInfo.virtualTourStatus !== VirtualTourStatus.Complete && (
                            <Button
                                addlClassName="btn-primary"
                                text="Complete Virtual Tour"
                                disabled={isUser}
                                onClick={async () => {
                                    if (prospect.publicKey === undefined) {
                                        setErrorMessage('Prospect has no associated ID.');
                                        return;
                                    }
                                    setTourChangeLoading(true);

                                    const result = await markVirtualTourCompleted(
                                        prospect.publicKey
                                    );
                                    if (result.success) {
                                        prospect.contactInfo.virtualTourStatus =
                                            VirtualTourStatus.Complete;
                                        updateSelectedProspect(prospect, false);
                                        closePopup();
                                    } else if (result.error) {
                                        setErrorMessage(result.error);
                                    }
                                    setTourChangeLoading(false);
                                }}
                            />
                        )}
                    {prospect &&
                        prospect.tourInfo &&
                        prospect.tourInfo.id &&
                        (userIsSystemAdmin() || prospect.tourInfo.status !== TourStatuses.Applied) ? (
                        <Button
                            addlClassName="btn-primary"
                            text={
                                prospect.tourInfo.applicationSubmitted
                                    ? 'Unmark Applied'
                                    : 'Mark Applied'
                            }
                            disabled={isUser}
                            onClick={async () => {
                                if (prospect.publicKey === undefined) {
                                    setErrorMessage('Prospect has no associated ID.');
                                    return;
                                }
                                setTourChangeLoading(true);
                                const result = await updateApplicationStatus(
                                    prospect.tourInfo.guid,
                                    !prospect.tourInfo.applicationSubmitted
                                );
                                if (result.success) {
                                    prospect.tourInfo.applicationSubmitted =  !prospect.tourInfo.applicationSubmitted;
                                    updateSelectedProspect(prospect, false);

                                    closePopup();
                                } else if (result.error) {
                                    setErrorMessage(result.error);
                                }
                                setTourChangeLoading(false);
                            }}
                        />
                    ) : null}
                    {prospect !== undefined && prospect.tourInfo.status === TourStatuses.NoShow && (
                        <Button
                            addlClassName="btn-primary"
                            text="Mark Tour Completed"
                            disabled={isUser}
                            onClick={async () => {
                                setTourChangeLoading(true);
                                if (prospect.publicKey === undefined) {
                                    setErrorMessage('Prospect has no associated ID.');
                                    setTourChangeLoading(false);
                                    return;
                                }

                                let completeResult = await markTourCompleted(prospect.publicKey);

                                if (completeResult.success) {
                                    prospect.tourInfo.status = TourStatuses.Complete;
                                    updateSelectedProspect(prospect, false);
                                    closePopup();
                                } else if (completeResult.error) {
                                    setErrorMessage(completeResult.error);
                                }
                                setTourChangeLoading(false);
                            }}
                        />
                    )}

                    <Button addlClassName="btn-primary" text="Close" onClick={closePopup} disabled={isUser}/>
                </div>
                <FormValidationDisplay errorMessage={errorMessage} />
            </ModalBody>
        </Modal>
    );
};



// WEBPACK FOOTER //
// ./src/components/pages/admin/dashboardWidgets/prospectPopup.tsx